import React, { useEffect, useState } from "react";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  CardDescription,
  Button,
  Input,
} from "../../../components/ui";
import {
  fetchLeadStages,
  createLeadStage,
  updateLeadStage,
} from "../../../services/crmSettings";

const emptyForm = {
  code: "",
  label: "",
  description: "",
  is_active: true,
  is_won: false,
  is_lost: false,
  sort_order: 100,
};

export default function LeadStagesSettings() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState(emptyForm);

  useEffect(() => {
    load();
  }, []);

  async function load() {
    try {
      setLoading(true);
      setError("");
      const rows = await fetchLeadStages();
      setItems(Array.isArray(rows) ? rows : []);
    } catch (e) {
      setError(e.message || "Failed to load lead stages");
      setItems([]);
    } finally {
      setLoading(false);
    }
  }

  function startEdit(item) {
    setEditingId(item.id);
    setForm({
      code: item.code || "",
      label: item.label || "",
      description: item.description || "",
      is_active: !!item.is_active,
      is_won: !!item.is_won,
      is_lost: !!item.is_lost,
      sort_order: item.sort_order ?? 100,
    });
  }

  function resetForm() {
    setEditingId(null);
    setForm(emptyForm);
  }

  async function onSubmit(e) {
    e.preventDefault();

    try {
      setSaving(true);
      setError("");

      const payload = {
        code: String(form.code || "").trim(),
        label: String(form.label || "").trim(),
        description: String(form.description || "").trim() || null,
        is_active: form.is_active ? 1 : 0,
        is_won: form.is_won ? 1 : 0,
        is_lost: form.is_lost ? 1 : 0,
        sort_order: Number(form.sort_order || 100),
      };

      if (!payload.code || !payload.label) {
        throw new Error("Code and label are required");
      }

      if (editingId) {
        await updateLeadStage(editingId, payload);
      } else {
        await createLeadStage(payload);
      }

      resetForm();
      await load();
    } catch (e) {
      setError(e.message || "Failed to save lead stage");
    } finally {
      setSaving(false);
    }
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle>Lead Stages</CardTitle>
        <CardDescription>Manage lead stages and order.</CardDescription>
      </CardHeader>

      <CardContent className="space-y-6">
        <div className="rounded-xl border">
          <div className="p-4 border-b">
            <div className="font-medium">Lead stage list</div>
            <div className="text-sm text-muted-foreground">
              Configure CRM lead stages.
            </div>
          </div>

          <div className="p-4">
            {error && (
              <div className="mb-4 rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700">
                {error}
              </div>
            )}

            {loading ? (
              <div className="text-sm text-muted-foreground">Loading...</div>
            ) : items.length === 0 ? (
              <div className="text-sm text-muted-foreground">No lead stages found.</div>
            ) : (
              <div className="space-y-3">
                {items.map((item) => (
                  <div
                    key={item.id}
                    className="rounded-xl border p-4 flex flex-col gap-3 md:flex-row md:items-start md:justify-between"
                  >
                    <div className="min-w-0">
                      <div className="flex flex-wrap items-center gap-2">
                        <div className="font-medium">{item.label}</div>

                        <span className="text-xs rounded-md border px-2 py-0.5">
                          {item.code}
                        </span>

                        {!item.is_active && (
                          <span className="text-xs rounded-md border px-2 py-0.5 text-muted-foreground">
                            inactive
                          </span>
                        )}

                        {!!item.is_won && (
                          <span className="text-xs rounded-md border px-2 py-0.5">
                            won
                          </span>
                        )}

                        {!!item.is_lost && (
                          <span className="text-xs rounded-md border px-2 py-0.5">
                            lost
                          </span>
                        )}
                      </div>

                      <div className="mt-2 text-sm text-muted-foreground">
                        {item.description || "—"}
                      </div>

                      <div className="mt-2 text-xs text-muted-foreground">
                        Sort: {item.sort_order ?? "—"}
                      </div>
                    </div>

                    <div className="flex gap-2">
                      <Button
                        variant="outline"
                        type="button"
                        onClick={() => startEdit(item)}
                      >
                        Edit
                      </Button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>

        <div className="rounded-xl border">
          <div className="p-4 border-b">
            <div className="font-medium">
              {editingId ? "Edit lead stage" : "New lead stage"}
            </div>
            <div className="text-sm text-muted-foreground">
              Create or update lead stage definition.
            </div>
          </div>

          <form onSubmit={onSubmit} className="p-4 space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <div>
                <div className="text-sm mb-1">Code</div>
                <Input
                  value={form.code}
                  onChange={(e) => setForm((s) => ({ ...s, code: e.target.value }))}
                  placeholder="new"
                />
              </div>

              <div>
                <div className="text-sm mb-1">Label</div>
                <Input
                  value={form.label}
                  onChange={(e) => setForm((s) => ({ ...s, label: e.target.value }))}
                  placeholder="New"
                />
              </div>
            </div>

            <div>
              <div className="text-sm mb-1">Description</div>
              <textarea
                rows={3}
                className="w-full rounded-md border px-3 py-2 bg-white dark:bg-neutral-900"
                value={form.description}
                onChange={(e) => setForm((s) => ({ ...s, description: e.target.value }))}
                placeholder="Short description..."
              />
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <div>
                <div className="text-sm mb-1">Sort order</div>
                <Input
                  type="number"
                  value={form.sort_order}
                  onChange={(e) =>
                    setForm((s) => ({ ...s, sort_order: Number(e.target.value || 100) }))
                  }
                />
              </div>

              <div className="flex items-end">
                <label className="flex items-center gap-2 text-sm">
                  <input
                    type="checkbox"
                    checked={form.is_active}
                    onChange={(e) =>
                      setForm((s) => ({ ...s, is_active: e.target.checked }))
                    }
                  />
                  Active
                </label>
              </div>
            </div>

            <div className="flex flex-wrap gap-6">
              <label className="flex items-center gap-2 text-sm">
                <input
                  type="checkbox"
                  checked={form.is_won}
                  onChange={(e) =>
                    setForm((s) => ({ ...s, is_won: e.target.checked }))
                  }
                />
                Won stage
              </label>

              <label className="flex items-center gap-2 text-sm">
                <input
                  type="checkbox"
                  checked={form.is_lost}
                  onChange={(e) =>
                    setForm((s) => ({ ...s, is_lost: e.target.checked }))
                  }
                />
                Lost stage
              </label>
            </div>

            <div className="flex justify-end gap-2 pt-2">
              {editingId && (
                <Button type="button" variant="outline" onClick={resetForm}>
                  Cancel
                </Button>
              )}

              <Button type="submit" disabled={saving}>
                {saving
                  ? "Saving..."
                  : editingId
                  ? "Update lead stage"
                  : "Create lead stage"}
              </Button>
            </div>
          </form>
        </div>
      </CardContent>
    </Card>
  );
}